import {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {AlertListItemProps} from './alertListItem.typings'
import {useStyles} from './styles'
import {Avatar, ListItem, Stack, Tooltip, Typography, useTheme} from '@mui/material'
import {createSnapshotFromUserAlert, getColorForAlertStatus, getDrawingFromAlert, getUserAlertKey, getUserAlertMessage, getUserAlertUnderlyingSecurity} from '../../../util/alerts'
import {formatUnixTimestamp} from '../../../util/dateTime'
import PlayCircleOutline from '../../../assets/icons/PlayCircleOutline'
import {AlertExpression_Type, UserAlert_Status} from '../../../proto/generated/nexus'
import {LOGOS_BASE_URL} from '../../../assets/constants'
import {getColorForSymbol} from '../../../util/general'
import {removeUserAlert, setAlertModalData, updateUserAlert} from '../../../reducer/alerts.reducer'
import {removeAlert, restartAlert, stopAlert} from '../../../services/apis/alerts'
import {selectIndicators} from '../../../selectors/chart'
import {getSecurityAsync, getSecurityKey} from '../../../util/security'
import commonActions from '../../../actions/common.actions'
import {NonInteractiveTooltip, UltraSmallButton} from '../../custom'
import PauseCircleOutline from '../../../assets/icons/PauseCircleOutline'
import DeleteIcon from '../../../assets/icons/DeleteIcon'
import AlertSettingsOutlinedIcon from '../../../assets/icons/AlertSettingOutlined'
import {getOperatorDisplayName} from '../alertOperatorDropdown/alertOperatorDropdown'
import ReusableDialog from '../../common/reusableDialog'
import {convertExpressionToDisplayExpression} from '../../../util/expression'
import {getTimeBucketDisplayName} from '../../../util/timeBuckets'
import {SecurityCategory_CategoryCode} from '../../../proto/generated/security'
import useAlertItemClick from '../../../hooks/useAlertItemClick'

const AlertListItem: React.FC<AlertListItemProps> = ({alert}) => {
    const {classes, cx} = useStyles()
    const dispatch = useDispatch()

    const indicators = useSelector(selectIndicators)
    const [tooltipConditionMessage, setTooltipConditionMessage] = useState('')

    const key = useMemo(() => getUserAlertKey(alert), [alert])
    const timeBucket = useMemo(() => alert.timeBucket, [alert.timeBucket])

    const [securityList, expression] = useMemo(() => getUserAlertUnderlyingSecurity(alert), [alert])
    const isExpression = useMemo(() => !!expression.length, [expression])
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const theme = useTheme()
    const [primarySecurity, setPrimarySecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [expressionSecurities, setExpressionSecurities] = useState<Awaited<ReturnType<typeof getSecurityAsync>>[]>([])
    useEffect(() => {
        if (!securityList.length) {
            setPrimarySecurity(undefined)
            setExpressionSecurities([])
            return
        }
        if (!isExpression) {
            getSecurityAsync(getSecurityKey(securityList[0].exchange, securityList[0].code)).then(setPrimarySecurity)
            setExpressionSecurities([])
        } else {
            Promise.all(securityList.map((v) => getSecurityAsync(getSecurityKey(v.exchange, v.code)))).then(setExpressionSecurities)
            setPrimarySecurity(undefined)
        }
    }, [securityList, isExpression])

    const symbolsDisplayValue = useMemo(() => {
        if (!securityList.length) return ''
        let elements = ''
        if (!isExpression && primarySecurity) {
            elements += primarySecurity.displayName
        } else if (isExpression && expression.length && expressionSecurities.length) {
            elements += convertExpressionToDisplayExpression(expression, expressionSecurities)
        }

        if (!elements.length) return ''
        if (timeBucket) elements += `, ${getTimeBucketDisplayName(timeBucket)}`
        return elements
    }, [securityList, expression, isExpression, timeBucket, primarySecurity, expressionSecurities])

    useEffect(() => {
        let cancelled = false
        if (!indicators?.length || !alert.condition?.securityAlertCondition) {
            setTooltipConditionMessage('')
            return
        }
        void getUserAlertMessage(alert.condition.securityAlertCondition, symbolsDisplayValue, indicators, {
            chartTimeBucket: alert.timeBucket,
        }).then((t) => {
            if (!cancelled) setTooltipConditionMessage(t)
        })
        return () => {
            cancelled = true
        }
    }, [alert, indicators, symbolsDisplayValue])

    const avatar = useMemo(() => {
        if (!securityList.length && !expression.length) return null
        const security = primarySecurity
        if (!security) return null
        let name = isExpression ? securityList.map((sec) => sec.code[0]).join('') : security.symbol || security.code
        if (isExpression || (!isExpression && !security.logoUrl)) {
            return (
                <Avatar alt={'Icon'} color={getColorForSymbol(isExpression ? key : securityList[0]?.symbol || securityList[0]?.code || '', theme.palette.background.default)} className={classes.avatar}>
                    {name
                        .split(' ')
                        .map((word) => word[0]?.toUpperCase())
                        .slice(0, 2)
                        .join('')}
                </Avatar>
            )
        }

        return <Avatar alt={'Icon'} src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`} className={classes.avatar}/>
    }, [securityList, expression, key, isExpression, primarySecurity, theme.palette.background.default])

    const activateAlertSecurity = useAlertItemClick()

    const onItemClick = useCallback(() => {
        activateAlertSecurity(alert)
    }, [activateAlertSecurity, alert])

    const handleActionClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation()
    }, [])

    const onDoubleClick = useCallback(async () => {
        try {
            const [underlyingSecurities] = getUserAlertUnderlyingSecurity(alert)
            const security = underlyingSecurities[0]
            if (security) {
                const actualSec = await getSecurityAsync(getSecurityKey(security.exchange, security.code))
                if (actualSec) {
                    const isOptionOrFuture = actualSec.categoryCode === SecurityCategory_CategoryCode.OPTIONS || actualSec.categoryCode === SecurityCategory_CategoryCode.FUTURES
                    if (isOptionOrFuture && actualSec.expiry && actualSec.expiry.expiry < Date.now() / 1000) {
                        commonActions.setApiError(dispatch, {error: true, message: 'Security is expired', errorType: 'error'})
                        return
                    }
                }
            }
            const chartSnapshot = createSnapshotFromUserAlert(alert, indicators)
            dispatch(
                setAlertModalData({
                    chartSnapshot,
                    edit: true,
                    alert,
                    sourceDrawing: getDrawingFromAlert(alert),
                })
            )
        } catch (error) {
            commonActions.setApiError(dispatch, {error: true, message: 'Failed to open alert editor', errorType: 'error'})
        }
    }, [alert, indicators, dispatch])

    const deleteAlert = useCallback(async () => {
        try {
            await removeAlert({
                id: alert.id
            })

            dispatch(removeUserAlert(alert.id))
        } catch (error) {
            commonActions.setApiError(dispatch, {error: true, message: 'Failed to remove alert', errorType: 'error'})
        }
    }, [alert.id])

    const onStopClick = useCallback(async () => {
        try {
            const updatedAlert = await stopAlert({
                id: alert.id
            })
            dispatch(updateUserAlert(updatedAlert))
        } catch (error) {
            commonActions.setApiError(dispatch, {error: true, message: 'Failed to stop alert', errorType: 'error'})
        }
    }, [alert.id])

    const onRestartClick = useCallback(async () => {
        try {
            const [underlyingSecurities] = getUserAlertUnderlyingSecurity(alert)
            const security = underlyingSecurities[0]
            if (security) {
                const actualSec = await getSecurityAsync(getSecurityKey(security.exchange, security.code))
                if (actualSec) {
                    const isOptionOrFuture = actualSec.categoryCode === SecurityCategory_CategoryCode.OPTIONS || actualSec.categoryCode === SecurityCategory_CategoryCode.FUTURES
                    if (isOptionOrFuture && actualSec.expiry && actualSec.expiry.expiry < Date.now() / 1000) {
                        commonActions.setApiError(dispatch, {error: true, message: 'Security is expired', errorType: 'error'})
                        return
                    }
                }
            }
            if (alert.expiry < Date.now()) {
                onDoubleClick()
                return
            }
            const updatedAlert = await restartAlert({
                id: alert.id
            })
            dispatch(updateUserAlert(updatedAlert))
        } catch (error) {
            commonActions.setApiError(dispatch, {error: true, message: 'Failed to restart alert', errorType: 'error'})
        }
    }, [alert.id, onDoubleClick])

    const onDeleteClick = useCallback(() => {
        setOpenDeleteDialog(true)
    }, [])

    const onDeleteDialogClose = useCallback(() => {
        setOpenDeleteDialog(false)
    }, [])

    const onDeleteDialogSubmit = useCallback(() => {
        setOpenDeleteDialog(false)
        deleteAlert()
    }, [deleteAlert])

    const displayName = useMemo(() => {
        if (!securityList.length) return ''
        if (expression.length) return expression
        return primarySecurity?.symbol || primarySecurity?.code || ''
    }, [securityList, expression, primarySecurity])

    const tooltipContent = useMemo(() => {
        const sac = alert.condition?.securityAlertCondition
        const operator = sac?.operator
        const drawingType =
            sac?.lhsExpression?.type === AlertExpression_Type.PATTERN ||
            sac?.rhsExpression?.type === AlertExpression_Type.PATTERN
                ? 'candlestickPattern'
                : sac?.rhsExpression?.type === AlertExpression_Type.DRAWING
                    ? sac.rhsExpression.drawing?.drawingType
                    : undefined
        const operatorLabel = operator != null ? getOperatorDisplayName(operator, drawingType) : ''
        const messageFromAlert = tooltipConditionMessage
        const operatorPosition = operatorLabel ? messageFromAlert.indexOf(operatorLabel) : -1

        return (
            <Stack className={classes.tooltip}>
                {alert.name && <Typography fontSize={'small'}>{alert.name}</Typography>}
                {alert.message && messageFromAlert !== alert.message && <Typography fontSize={'small'}>{alert.message}</Typography>}
                {alert.condition?.securityAlertCondition && (
                    <Stack direction="row" gap={0.5} className={classes.fullWidth}>
                        <Typography fontSize={'small'} component={'span'} className={classes.wrapOverflow}>
                            {operatorPosition >= 0 ? messageFromAlert.substring(0, operatorPosition) : messageFromAlert}
                            {operatorPosition >= 0 && (
                                <Typography component={'span'} color={'warning.light'} fontSize={'small'}>
                                    {operatorLabel}
                                </Typography>
                            )}
                            {operatorPosition >= 0 ? messageFromAlert.substring(operatorPosition + operatorLabel.length) : ''}
                        </Typography>
                    </Stack>
                )}
                <Stack direction="row" gap={0.5} className={classes.fullWidth}>
                    <Typography variant={'caption'} className={classes.wrapOverflow}>
                        <Typography component={'span'} color={getColorForAlertStatus(alert.secondaryStatus, theme.palette.mode === 'light')} variant="caption">
                            {alert.secondaryStatus}
                        </Typography>
                        {`${'\u00B7'} ${key}`}
                    </Typography>
                </Stack>
                <Typography variant={'caption'}
                            className={cx(classes.fullWidth, classes.wrapOverflow, classes.tooltipCaption)}>{`Created: ${formatUnixTimestamp(alert.created)}`}</Typography>
                {!!alert.lastTriggered && (
                    <Typography
                        variant={'caption'}
                        className={cx(classes.fullWidth, classes.wrapOverflow, classes.tooltipCaption)}
                    >{`Last triggered: ${formatUnixTimestamp(alert.lastTriggered)}`}</Typography>
                )}
            </Stack>
        )
    }, [alert, key, displayName, tooltipConditionMessage, classes])

    const deleteDialogContent = useMemo(() => {
        return `Do you really want to delete your alert '${alert.name ? alert.name : alert.message ? alert.message : ''}'?`.trim()
    }, [alert])

    const alertButtons = useMemo(() => {
        return (
            <Stack direction={'row'} gap={0.5} className={cx(classes.displayOnHover, classes.minW0)}>
                {alert.status === UserAlert_Status.STOPPED ? (
                    <NonInteractiveTooltip title={'Restart'} arrow placement={'top'}>
                        <UltraSmallButton
                            onClick={onRestartClick}
                            color="inherit"
                            aria-label="play"
                            data-event={'Alert Resumed'}
                            data-event-alert-name={alert.name}
                            data-event-alert-message={alert.message}
                            className={classes.hoveredButton}
                        >
                            <PlayCircleOutline className={classes.hoveredIcon}/>
                        </UltraSmallButton>
                    </NonInteractiveTooltip>
                ) : (
                    <NonInteractiveTooltip title={'Stop'} arrow placement={'top'}>
                        <UltraSmallButton
                            onClick={onStopClick}
                            color="inherit"
                            aria-label="pause"
                            data-event={'Alert Paused'}
                            data-event-alert-name={alert.name}
                            data-event-alert-message={alert.message}
                            className={classes.hoveredButton}
                        >
                            <PauseCircleOutline className={classes.hoveredIcon}/>
                        </UltraSmallButton>
                    </NonInteractiveTooltip>
                )}

                <NonInteractiveTooltip title={'Edit'} arrow placement={'top'}>
                    <UltraSmallButton
                        onClick={onDoubleClick}
                        color="inherit"
                        aria-label="close"
                        data-event={'Alert Edit Clicked'}
                        data-event-alert-name={alert.name}
                        data-event-alert-message={alert.message}
                        className={classes.hoveredButton}
                    >
                        <AlertSettingsOutlinedIcon className={classes.hoveredIcon}/>
                    </UltraSmallButton>
                </NonInteractiveTooltip>

                <NonInteractiveTooltip title={'Delete'} arrow placement={'top'}>
                    <UltraSmallButton
                        onClick={onDeleteClick}
                        color="inherit"
                        aria-label="close"
                        data-event={'Alert Deleted'}
                        data-event-alert-name={alert.name}
                        data-event-alert-message={alert.message}
                        className={classes.hoveredButton}
                    >
                        <DeleteIcon className={classes.hoveredIcon}/>
                    </UltraSmallButton>
                </NonInteractiveTooltip>
            </Stack>
        )
    }, [alert.status, alert.name, onRestartClick, classes, onStopClick, onDoubleClick, onDeleteClick])

    const hoverButtons = useMemo(() => {
        return (
            <Stack direction={'row'} className={classes.hoveredButtons} onClick={handleActionClick}>
                {alertButtons}
            </Stack>
        )
    }, [alertButtons, classes, handleActionClick])

    const nameAndMessage = useMemo(() => {
        return (
            <Stack className={classes.fullWidth}>
                {
                    (alert.message && alert.name) && (<Stack className={cx(classes.fullWidth, classes.alignCenter, classes.h22)} direction={'row'}>
                        <Typography className={cx('font-feature-num', classes.alertMessage, classes.minW0)}>{alert.message}</Typography>
                        {alert?.name && hoverButtons}
                    </Stack>)
                }
                {
                    (alert.message && !alert.name) && (<Stack className={cx(classes.fullWidth, classes.alignCenter, classes.h22)} direction={'row'}>
                        <Typography className={cx('font-feature-num', classes.alertMessage, classes.minW0)}>{alert.message}</Typography>
                        {!alert?.name && hoverButtons}
                    </Stack>)
                }
                {(!alert?.message) && (
                    <Stack gap={0.5} direction="row" className={cx(classes.attributes, classes.alignCenter)}>
                        {avatar}
                        <Typography variant={'caption'} color={''}>
                            {displayName}
                        </Typography>
                        {alert.secondaryStatus && (
                            <Typography className={classes.dot} variant={'caption'}>
                                {'\u00B7'}
                            </Typography>
                        )}
                        {alert.secondaryStatus && (
                            <Typography variant={'caption'} color={getColorForAlertStatus(alert.secondaryStatus)}>
                                {alert.secondaryStatus}
                            </Typography>
                        )}
                        {!!alert.lastTriggered ? (
                            <>
                                <Typography variant="caption" className={classes.dot}>
                                    {'\u00B7'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '90px',
                                    }}
                                >
                                    {formatUnixTimestamp(alert.lastTriggered)}
                                </Typography>
                            </>
                        ) : null}
                        {hoverButtons}

                    </Stack>
                )}
            </Stack>
        )
    }, [alert?.name, alert?.message, classes, hoverButtons, avatar, displayName, alert.secondaryStatus, alert.lastTriggered, cx])

    return (
        <>
            <Tooltip title={tooltipContent} arrow={true} placement="left" enterDelay={700} enterNextDelay={0}>
                <ListItem
                    key={alert.id}
                    className={cx(classes.parentContainer)}
                    onClick={onItemClick}
                    onDoubleClick={onDoubleClick}
                    sx={{
                        cursor: 'default',
                        '&.MuiListItem-button': {
                            cursor: 'default',
                        },
                        color: (theme) => (alert.status !== UserAlert_Status.ACTIVE ? theme.palette.text.secondary : undefined)
                    }}
                >
                    {nameAndMessage}
                    <Stack gap={0.5} direction="row" className={classes.attributes}>
                        {alert.message && avatar}
                        {alert.message &&
                            <Typography variant={'caption'} color={''}>
                                {displayName}
                            </Typography>
                        }
                        {alert.message && alert.secondaryStatus && (
                            <Typography className={classes.dot} variant={'caption'}>
                                {'\u00B7'}
                            </Typography>
                        )}
                        {alert.message && alert.secondaryStatus && (
                            <Typography variant={'caption'} color={getColorForAlertStatus(alert.secondaryStatus)}>
                                {alert.secondaryStatus}
                            </Typography>
                        )}
                        {alert.message && !!alert.lastTriggered && (
                            <Typography variant={'caption'} className={classes.dot}>
                                {'\u00B7'}
                            </Typography>
                        )}
                        {alert.message && !!alert.lastTriggered && (
                            <Typography variant={'caption'} color={'text.secondary'}>
                                {formatUnixTimestamp(alert.lastTriggered)}
                            </Typography>
                        )}
                    </Stack>
                </ListItem>
            </Tooltip>

            <ReusableDialog
                key={`delete-alert-dialog-${alert.id}`}
                open={openDeleteDialog}
                onClose={onDeleteDialogClose}
                onSubmit={onDeleteDialogSubmit}
                title={'Delete alert'}
                content={deleteDialogContent}
                submitText={'Delete'}
                submitButtonProps={{
                    color: 'error',
                }}
                dialogContentSx={{ pt: 0, px: 2.5, pb: 1 }}
                dialogActionsSx={{ px: 2.5, pb: 2, pt: 1 }}
                cancelVariant={'outlined'}
                cancelText={'No'}
            />
        </>
    )
}
export default memo(AlertListItem)
